import React, { useCallback, useEffect, useState } from "react";
import { VariableSizeList as _VariableSizeList } from "react-window";
import { VariableSizeListProps } from "react-window";

import { flattenHierarchy } from "./hierarchical-list-for-filter";
import { HierarchicalListItem } from "./hierarchical-list-for-filter";


const VariableSizeList = _VariableSizeList as unknown as React.ComponentType<VariableSizeListProps>;

interface HierarchicalFilterListProps {
  items: any[];
  searchedValue: string;
  isChecked: (id: number) => boolean;
  applyFilter: (id: number, checked: boolean) => void;
  listWidth: number;
  listHeight: number;
}

export const HierarchicalFilterList: React.FC<HierarchicalFilterListProps> = ({
  items,
  searchedValue,
  isChecked,
  applyFilter,
  listWidth,
  listHeight,
}) => {
  // Initialize with main categories expanded
  const [expandedItems, setExpandedItems] = useState<number[]>([]);
  
  const [flattenedItems, setFlattenedItems] = useState<any[]>([]);
  
  // Create hierarchical structure from flat data
  const createHierarchy = (flatItems: any[]) => {
    const itemMap = new Map();
    const rootItems: any[] = [];

    // First pass: create a map of all items by id
    flatItems.forEach(item => {
      // Create a copy of the item with a children array
      const newItem = { ...item, children: [] };
      itemMap.set(item.id, newItem);
    });

    // Add all items that don't have a parent as root items
    flatItems.forEach(item => {
      if (!item.parentId) {
        const rootItem = itemMap.get(item.id);
        if (rootItem) {
          rootItems.push(rootItem);
        }
      }
    });

    // Create parent-child relationships based on parentId
    flatItems.forEach(item => {
      if (item.parentId && item.parentId !== item.id) {
        const parentItem = itemMap.get(item.parentId);
        const childItem = itemMap.get(item.id);
        
        if (parentItem && childItem) {
          parentItem.children.push(childItem);
        }
      }
    });

    return rootItems;
  };
  
  // Update expanded items based on checked status
  useEffect(() => {
    // Create the hierarchical structure
    const hierarchy = createHierarchy(items);
    
    // Find all parent items
    const findAllParents = (items: any[], visited: Set<number> = new Set()): number[] => {
      return items.reduce((acc: number[], item) => {
        if (visited.has(item.id)) return acc;
        visited.add(item.id);
        if (item.children && item.children.length > 0) {
          acc.push(item.id);
          acc.push(...findAllParents(item.children, visited));
        }
        return acc;
      }, []);
    };
    
    const allParents = findAllParents(hierarchy);
    
    // Check which parents are currently selected
    const selectedParents = allParents.filter(id => isChecked(id));
    
    // Update expanded items to only include selected parents
    setExpandedItems(prevExpanded => {
      // Keep only parents that are checked
      const newExpanded = prevExpanded.filter(id => 
        selectedParents.includes(id)
      );
      
      // Add any newly selected parents
      selectedParents.forEach(id => {
        if (!newExpanded.includes(id)) {
          newExpanded.push(id);
        }
      });
      
      return newExpanded;
    });
  }, [items, isChecked]);
  
  useEffect(() => {
    // Create the hierarchical structure
    const hierarchy = createHierarchy(items);
    
    // Generate the flattened list based on expanded items
    const flattened = flattenHierarchy(hierarchy, expandedItems);
    setFlattenedItems(flattened);
  }, [items, expandedItems]);
  
  const toggleExpand = useCallback((id: number) => {
    // Check if this is a new expansion or collapsing
    const isExpanding = !expandedItems.includes(id);
    
    // If expanding, also select this item
    if (isExpanding) {
      applyFilter(id, true);
    }
    
    setExpandedItems(prevExpanded => {
      if (prevExpanded.includes(id)) {
        return prevExpanded.filter(itemId => itemId !== id);
      } else {
        return [...prevExpanded, id];
      }
    });
  }, [applyFilter, expandedItems]);
  
  const handleApplyFilter = (id: number, checked: boolean) => {
    // Apply the filter for this item
    applyFilter(id, checked);
    
    // Create the hierarchical structure
    const hierarchy = createHierarchy(items);
    
    // If unchecking a parent, also uncheck all children
    if (!checked) {
      const findAndUncheckChildren = (items: any[], targetId: number) => {
        for (const item of items) {
          if (item.id === targetId && item.children && item.children.length > 0) {
            // Uncheck all children recursively
            item.children.forEach((child: any) => {
              applyFilter(child.id, false);
              if (child.children && child.children.length > 0) {
                findAndUncheckChildren([child], child.id);
              }
            });
            return true;
          }
          
          if (item.children && item.children.length > 0) {
            if (findAndUncheckChildren(item.children, targetId)) {
              return true;
            }
          }
        }
        
        return false;
      };
      
      findAndUncheckChildren(hierarchy, id);
      
      // Also handle parent-child relationship for siblings
      const findParentAndCheckSiblings = (items: any[], childId: number): boolean => {
        for (const item of items) {
          if (item.children && item.children.length > 0) {
            // Check if this item is the parent of our target
            const isParent = item.children.some((child: any) => child.id === childId);
            
            if (isParent) {
              return true;
            }
            
            // Search deeper in the hierarchy
            if (findParentAndCheckSiblings(item.children, childId)) {
              return true;
            }
          }
        }
        
        return false;
      };
      
      findParentAndCheckSiblings(hierarchy, id);
    }
  };
  
  return (
    <VariableSizeList
      height={listHeight}
      width={"100%"}
      itemSize={() => 30} // All items have consistent 30px height
      itemCount={flattenedItems.length}
      overscanCount={5}
      style={{
        overflowX: "hidden"
      }}
      itemData={{
        flattenedItems,
        searchedValue,
        isChecked,
        applyFilter: handleApplyFilter,
        toggleExpand,
        expandedItems,
        listWidth
      }}
    >
      {HierarchicalListItem}
    </VariableSizeList>
  );
}; 
